<template>
  <BDrawerMenu
    v-model="drawer"
    :title="$t('nextActionSuggestion.title')"
    @cancel="handleCancelClick"
  >
    <div>
      <div class="call-result-detail-link-container">
        <CallResultDetailLink
          :call-result-id="nextActionSuggestion?.callResultId"
        />
      </div>
      <NextActionFormBody
        v-model="nextActionSuggestionBody"
        v-model:is-valid="nextActionFormBodyValid"
        :call-target-id="nextActionSuggestion?.callTargetId"
      >
        <template #dateTime>
          <BListItem class="mb-300">
            <template #header>
              <span>{{ $t('nextAction.reservedAt') }}</span>
            </template>
            <BInputDateTime
              v-model:date="reservedDate"
              v-model:time="reservedTime"
              is-step
              clearable
            />
          </BListItem>
        </template>
      </NextActionFormBody>
      <BListItem v-if="nextActionSuggestion?.id">
        <template #header>
          <span>{{ $t('nextAction.createdAt') }}</span><span>{{ formatShorterDateTime(nextActionSuggestion?.firstSetUpAt) }}</span>
        </template>
      </BListItem>
    </div>
    <template #footer>
      <div
        class="footer"
      >
        <div class="footer-row">
          <BBtn
            outline
            :disabled="waiting"
            type="error"
            @click="handleRejectClick"
          >
            {{ $t('general.reject.text') }}
          </BBtn>
          <BBtn
            :disabled="!isFormValid"
            type="primary"
            :loading="waiting"
            @click="handleApproveClick"
          >
            {{ $t('general.approve.text') }}
          </BBtn>
        </div>
        <div class="footer-row">
          <BBtn
            text
            :disabled="waiting"
            @click="handleCancelClick"
          >
            {{ $t('general.cancel.text') }}
          </BBtn>
        </div>
      </div>
    </template>
  </BDrawerMenu>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { NextActionSuggestion } from '@/api/openapi';
import { NextActionSuggestionApiService } from '@/api/user/resources/next_action_suggestion';
import CallResultDetailLink from '@/components/organisms/user/general/CallResultDetailLink.vue';
import NextActionFormBody, { TCommonNextActionWithOwnerNoMergedActionType } from '@/components/organisms/user/general/NextActionFormBody.vue';
import { useConfirm } from '@/composable/message-dialog';
import { useWait } from '@/composable/vue-wait';
import { key } from '@/store/index';
import { mergeDateAndTime, formatShorterDateTime } from '@/utils/date-time';

type TEmit = {
  'update:nextActionSuggestion': [nextActionSuggestion: NextActionSuggestion];
};

type TProps = {
  nextActionSuggestion: NextActionSuggestion;
};
const props = defineProps<TProps>();
const emit = defineEmits<TEmit>();

const { confirm } = useConfirm();
const i18n = useI18n();

const buildNextActionSuggestionBody = (nextActionSuggestion: NextActionSuggestion | null): TCommonNextActionWithOwnerNoMergedActionType => {
  return {
    actionTypeId: nextActionSuggestion?.actionTypeId,
    defaultActionTypeId: nextActionSuggestion?.defaultActionTypeId,
    priority: nextActionSuggestion?.priority,
    ownerId: nextActionSuggestion?.owner.id,
    content: nextActionSuggestion?.content,
  };
};
const nextActionSuggestionBody = ref<TCommonNextActionWithOwnerNoMergedActionType | null>(buildNextActionSuggestionBody(props.nextActionSuggestion));

const reservedAt = ref<Date | null>(null);

const nextActionFormBodyValid= ref(false);

const store = useStore(key);
const { doActionWithWait, wait } = useWait();

const reservedDate = computed({
  get: () => reservedAt.value,
  set: (newVal) => {
    const date = newVal;
    const time = reservedTime.value || newVal;
    reservedAt.value = mergeDateAndTime(date, time);
  },
});

const reservedTime = computed({
  get: () => reservedAt.value,
  set: (newVal) => {
    const date = reservedDate.value || newVal;
    const time = newVal;
    reservedAt.value = mergeDateAndTime(date, time);
  },
});

const drawer = computed({
  get: () => store.getters['userUi/targetNextActionSuggestionFormVisible'],
  set: (newVal) => store.dispatch('userUi/setTargetNextActionSuggestionFormVisibleAction', newVal),
});

const waiting = computed(() => {
  return wait.is(['rejectWait', 'approveWait']);
});

const isFormValid = computed(() => {
  return nextActionFormBodyValid.value && !!reservedAt.value;
});

const handleCancelClick = async () => {
  const accepted = await confirm({
    title: i18n.t('general.confirm.text'),
    message: i18n.t('general.alert.of', { target: i18n.t('general.unsavedValue'), action: i18n.t('general.clear') }),
  });
  if (!accepted) return;
  closeDrawer();
};

const handleRejectClick = async () => {
  const accepted = await confirm({
    message: i18n.t('nextActionSuggestion.confirmRejection.message'),
  });
  if (!accepted) return;
  await doActionWithWait('rejectWait', async () => {
    const api = new NextActionSuggestionApiService();
    await api.postNextActionSuggestionReject({
      request: {
        nextActionSuggestionId: props.nextActionSuggestion.id,
      },
    });
    closeDrawer();
    emit('update:nextActionSuggestion', props.nextActionSuggestion);
  });
};

const handleApproveClick = async () => {
  await doActionWithWait('approveWait', async () => {
    const api = new NextActionSuggestionApiService();
    await api.postNextActionSuggestionApprove({
      request: {
        nextActionSuggestionId: props.nextActionSuggestion.id,
        postNextActionSuggestionApproveRequest: {
          actionTypeId: nextActionSuggestionBody.value.actionTypeId,
          defaultActionTypeId: nextActionSuggestionBody.value.defaultActionTypeId,
          priority: nextActionSuggestionBody.value.priority,
          ownerId: nextActionSuggestionBody.value.ownerId,
          reservedAt: reservedAt.value?.toLocaleString(),
          content: nextActionSuggestionBody.value.content,
        },
      },
    });
    emit('update:nextActionSuggestion', props.nextActionSuggestion);
    closeDrawer();
  });
};

const closeDrawer = () => {
  drawer.value = false;
  nextActionSuggestionBody.value = buildNextActionSuggestionBody(null);
};

watch(drawer, () => {
  nextActionSuggestionBody.value = buildNextActionSuggestionBody(props.nextActionSuggestion);
  reservedDate.value = new Date(props.nextActionSuggestion?.reservedAt);
  reservedTime.value = new Date(props.nextActionSuggestion?.reservedAt);
});
</script>

<style lang="scss" scoped>
.footer {
  background-color: $bgcolor-base;
  padding: $basespace-200 $basespace-400;
  border-top: 1px solid $bdcolor-light;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: $basespace-200;
}

.footer-row {
  display: flex;
  justify-content: center;
  gap: $basespace-300;
}

.call-result-detail-link-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: $basespace-50;
}
</style>
